// Les libelles du calendrier de conservation : la classe et le declencheur du catalogue
// (`packages/etl/src/catalog/lineage.ts`, CLASSES_DE_CONSERVATION et
// DECLENCHEURS_DE_CONSERVATION) dits en francais et en anglais.
//
// Cles LITTERALES (i18n:check ne suit pas une cle construite) : une classe ou un declencheur
// ajoute au catalogue sans son libelle ici s affiche avec la description que le serveur rend
// (classe) ou son code (declencheur) — jamais une cle brute.
import type { TFunction } from 'i18next';

export function libelleDeClasse(code: string, t: TFunction, repli?: string): string {
  switch (code) {
    case 'emploi': return t('admin:calendrier.classe.emploi');
    case 'remuneration': return t('admin:calendrier.classe.remuneration');
    case 'equite-salariale': return t('admin:calendrier.classe.equiteSalariale');
    case 'compte-utilisateur': return t('admin:calendrier.classe.compteUtilisateur');
    case 'contrat-client': return t('admin:calendrier.classe.contratClient');
    case 'registre-comptable': return t('admin:calendrier.classe.registreComptable');
    case 'actif-de-l-entreprise': return t('admin:calendrier.classe.actifDeLEntreprise');
    case 'vie-d-equipe': return t('admin:calendrier.classe.vieDEquipe');
    case 'communication-interne': return t('admin:calendrier.classe.communicationInterne');
    case 'copie-de-source': return t('admin:calendrier.classe.copieDeSource');
    case 'reference': return t('admin:calendrier.classe.reference');
    case 'journal-d-acces': return t('admin:calendrier.classe.journalDAcces');
    case 'journal-technique': return t('admin:calendrier.classe.journalTechnique');
    case 'jeton': return t('admin:calendrier.classe.jeton');
    case 'paie': return t('admin:calendrier.classe.paie');
    case 'fiscal': return t('admin:calendrier.classe.fiscal');
    case 'candidature': return t('admin:calendrier.classe.candidature');
    case 'enquete-accident': return t('admin:calendrier.classe.enqueteAccident');
    case 'prevention': return t('admin:calendrier.classe.prevention');
    case 'incident-confidentialite': return t('admin:calendrier.classe.incidentConfidentialite');
    case 'sauvegarde': return t('admin:calendrier.classe.sauvegarde');
    case 'demande-acces': return t('admin:calendrier.classe.demandeAcces');
    case 'anonymisation': return t('admin:calendrier.classe.anonymisation');
    case 'dossier-sst': return t('admin:calendrier.classe.dossierSst');
    case 'dossier-medical': return t('admin:calendrier.classe.dossierMedical');
    case 'premiers-secours': return t('admin:calendrier.classe.premiersSecours');
    default: return repli ?? code;
  }
}

export function libelleDeDeclencheur(code: string, t: TFunction): string {
  switch (code) {
    case 'fin-d-emploi': return t('admin:calendrier.declencheur.finDEmploi');
    case 'desactivation-du-compte': return t('admin:calendrier.declencheur.desactivationDuCompte');
    case 'fin-du-contrat': return t('admin:calendrier.declencheur.finDuContrat');
    case 'fin-de-l-exercice-financier': return t('admin:calendrier.declencheur.finDeLExerciceFinancier');
    case 'cloture-de-l-exercice-d-equite': return t('admin:calendrier.declencheur.clotureDeLExerciceDEquite');
    case 'retrait-de-l-actif': return t('admin:calendrier.declencheur.retraitDeLActif');
    case 'fermeture-du-billet': return t('admin:calendrier.declencheur.fermetureDuBillet');
    case 'creation': return t('admin:calendrier.declencheur.creation');
    case 'retrait-a-la-source': return t('admin:calendrier.declencheur.retraitALaSource');
    case 'remplacement': return t('admin:calendrier.declencheur.remplacement');
    case 'expiration': return t('admin:calendrier.declencheur.expiration');
    case 'fin-de-l-annee-d-imposition': return t('admin:calendrier.declencheur.finDeLAnneeDImposition');
    case 'decision-de-ne-pas-retenir': return t('admin:calendrier.declencheur.decisionDeNePasRetenir');
    case 'fermeture-de-l-enquete': return t('admin:calendrier.declencheur.fermetureDeLEnquete');
    case 'connaissance-de-l-incident': return t('admin:calendrier.declencheur.connaissanceDeLIncident');
    case 'reponse-a-la-demande': return t('admin:calendrier.declencheur.reponseALaDemande');
    case 'fermeture-du-dossier-d-indemnisation': return t('admin:calendrier.declencheur.fermetureDuDossierDIndemnisation');
    default: return code;
  }
}

/** « 7 ans », « 18 mois », « 1 year » : le pluriel vient d Intl, pas d une cle par nombre. */
export function dureeLisible(mois: number, langue: string): string {
  const unite = mois % 12 === 0 ? 'year' : 'month';
  const valeur = unite === 'year' ? mois / 12 : mois;
  return new Intl.NumberFormat(langue, { style: 'unit', unit: unite, unitDisplay: 'long' }).format(valeur);
}

/** Le refus du serveur, par son code metier, dit a l usager ; jamais le message brut. */
export function libelleDeRefus(code: string | null, t: TFunction): string {
  switch (code) {
    case 'CALENDRIER_VERSION_PERIMEE': return t('admin:calendrier.refus.versionPerimee');
    case 'CALENDRIER_LIGNE_EXISTE': return t('admin:calendrier.refus.ligneExiste');
    case 'CALENDRIER_RIEN_A_CHANGER': return t('admin:calendrier.refus.rienAChanger');
    case 'CALENDRIER_LIGNE_RETIREE': return t('admin:calendrier.refus.ligneRetiree');
    case 'CALENDRIER_DEJA_RETIREE':
    case 'CALENDRIER_DEJA_ACTIVE': return t('admin:calendrier.refus.dejaFait');
    case 'CALENDRIER_JURIDICTION_INCONNUE':
    case 'CALENDRIER_REGIME_INCONNU': return t('admin:calendrier.refus.juridiction');
    case 'CALENDRIER_AUTEUR_SANS_IDENTIFIANT': return t('admin:calendrier.refus.auteur');
    case 'VALIDATION_ERROR':
    case 'CALENDRIER_REGLE_REFUSEE': return t('admin:calendrier.refus.invalide');
    default: return t('admin:calendrier.refus.echec');
  }
}

/** Le code metier porte par le corps de la reponse (format legacy `{ code }`). */
export function codeDuRefus(err: unknown): string | null {
  const corps = (err as { originalBody?: unknown } | null)?.originalBody;
  const code = (corps as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' ? code : null;
}
